"use client";

import Close from "@/components/icons/Close";
import Button from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import {
  ProductCardList,
  ProductCardListSkeleton,
} from "@/features/product/components/ProductCardList";
import { useTranslations } from "@/i18n/client";
import { Link } from "@/i18n/routing";
import type { ProductCard as ProductCardType } from "@/lib/centra/formatters";
import { Slot } from "@radix-ui/react-slot";
import clsx from "clsx";

import { ProductListingPageStoryblok } from "@/generated/sb/sb-types";
import { usePathname, useRouter } from "@/i18n/routing";
import { ContentLink } from "@depict-ai/types/api/SearchResponse";
import { SearchSuggestionsResponseV3 } from "@depict-ai/types/api/SearchSuggestionsResponseV3";
import { DepictErrorMessage } from "@frend-digital/depict/server";
import { Err, Ok } from "@frend-digital/utils/results";
import { ISbStoryData } from "@storyblok/js";
import React, {
  ComponentProps,
  Fragment,
  useDeferredValue,
  useEffect,
  useRef,
  useState,
  useTransition,
} from "react";
import { create } from "zustand";
import {
  getSearchUrl,
  useProductSearch,
  useSearchUrl,
} from "../hooks/useProductSearch";
import styles from "./index.module.css";

interface SearchModalState {
  open: boolean;
  openPath: string | null;

  setOpen: (isOpen: boolean) => void;
}

export const useSearchModal = create<SearchModalState>((set) => ({
  open: false,
  openPath: null,
  setOpen: (open) =>
    set({ open, openPath: open ? window.location.pathname : null }),
}));

interface SearchModalProps {
  title?: string;
  categoryStories: ISbStoryData<ProductListingPageStoryblok>[];
  featuredProducts?: ProductCardType[];
}

export const SearchModal = ({ categoryStories, title }: SearchModalProps) => {
  const open = useSearchModal((state) => state.open);
  const setOpen = useSearchModal((state) => state.setOpen);
  const ref = useRef<HTMLDivElement>(null!);
  const inputRef = useRef<HTMLInputElement>(null!);
  const router = useRouter();
  const [query, setQuery] = useState("");
  const [isPending, startTransition] = useTransition();
  const pathname = usePathname();
  const t = useTranslations();

  // Open close state
  useEffect(() => {
    const aside = ref.current;
    if (open) {
      aside.scrollTo({ top: 0 });
      // Timeout is needed for browser to focus
      setTimeout(() => {
        inputRef.current.focus();
      }, 100);
    } else {
      // After closing wipe query
      setQuery("");
    }

    return () => {};
  }, [open]);

  /**
   * Close the modal if the user navigates away
   */
  useEffect(() => {
    const state = useSearchModal.getState();
    if (state.openPath !== pathname && state.open) {
      setOpen(false);
    }
    // React only to pathname change
  }, [pathname, setOpen]);

  /**
   * Close the modal on submit and navigate to the search page
   */
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (query) {
      startTransition(() => {
        router.push(getSearchUrl(query));
      });
    }
  };
  const pendingRef = useRef(isPending);
  useEffect(() => {
    if (pendingRef.current) {
      setOpen(false);
    }

    pendingRef.current = isPending;
  }, [isPending, setOpen]);

  return (
    <aside
      ref={ref}
      className={styles.searchModal}
      data-state={open ? "open" : "closed"}
      // Accessibility
      role="dialog"
      {...{ inert: open ? false : true }} // Invalid @types/react
    >
      <div className={styles.content}>
        <SearchClose />
        <section className={clsx(styles.searchField)}>
          <form onSubmit={handleSubmit}>
            <Input
              ref={inputRef}
              className={styles.input}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onClear={() => setQuery("")}
              variant="search"
              type="search"
              name="search"
              placeholder={t("search.title")}
            />
          </form>
        </section>
        <ProductSearchList
          categoryStories={categoryStories}
          query={query}
          title={title}
        />
      </div>
    </aside>
  );
};

export const MODAL_MAX_COUNT = 4;

const ProductSearchList = ({
  categoryStories,
  query,
}: SearchModalProps & { query: string }) => {
  const searchTerm = useDeferredValue(query);

  return (
    <section
      key={!!searchTerm ? "search" : "featured"}
      className={styles.productSection}
    >
      <div key="product-search-results">
        <ProductSearchResults
          categoryStories={categoryStories}
          query={searchTerm}
        />
        <DoNotRemove />
      </div>
    </section>
  );
};

const ProductSearchResults = ({
  query,
  categoryStories,
}: {
  query: string;
  categoryStories: ISbStoryData<ProductListingPageStoryblok>[];
}) => {
  const { data, categoriesSuggestions, isFetching, isLoading } =
    useProductSearch(query, {
      enabled: true,
    });

  const url = useSearchUrl(query);
  const t = useTranslations();

  if (isLoading)
    return (
      <Fragment key="product-skeleton">
        <h2 className={styles.searchTitle}>{t("search.results")}</h2>
        <ProductCardListSkeleton data-layout="none" />
      </Fragment>
    );

  if (!isFetching && !data?.products.length && query) {
    console.log("No products found for query:", query);
    return (
      <p className={styles.noResults}>
        No matches found. Try to rephrase or{" "}
        <Link href="/products">browse all products.</Link>
      </p>
    );
  }

  return (
    <Fragment key="products-search-results">
      {data?.contentLinks && data?.contentLinks?.length > 0 && (
        <SearchContentResults contentLinks={data?.contentLinks} />
      )}

      <SearchCategoryResults
        categoryStories={categoryStories}
        data={categoriesSuggestions}
        query={query}
      />
      <h2 className={clsx(styles.searchTitle)}>
        {!query ? t("search.featured") : t("search.results")}
      </h2>

      <ProductCardList
        data-layout="none"
        cards={data?.products?.slice(0, 4) || []}
      />
      {data?.products && (data?.products.length ?? 0) >= MODAL_MAX_COUNT && (
        <Button mode="link" className={styles.button} href={url}>
          {t("search.seeAllResults")}
        </Button>
      )}
    </Fragment>
  );
};

export const SearchCategoryResults = ({
  categoryStories,
  data,
}: {
  query: string;
  categoryStories: ISbStoryData<ProductListingPageStoryblok>[];
  data: Ok<SearchSuggestionsResponseV3> | Err<DepictErrorMessage> | undefined;
}) => {
  const t = useTranslations();
  if (typeof data === "undefined") return null;
  if (data.err || !data?.val?.suggestions?.length) return null;

  const categoriesToShow = data?.val?.suggestions
    .map((suggestion) => {
      const listing = suggestion as unknown as {
        listing_id: string;
      };
      if (!listing.listing_id) return undefined;
      return categoryStories.find(
        (story) =>
          story.content.depictCategory ===
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (listing.listing_id as unknown as any),
      );
    })
    .filter(Boolean);

  if (!categoriesToShow || categoriesToShow.length === 0) return null;

  return (
    <section className={styles.productSection}>
      <h2 className={styles.searchTitle}>{t("Categories")}</h2>
      <div
        /*   style={{ paddingBottom: "32px" }} */
        className={styles.searchSuggestions}
      >
        {categoriesToShow?.map((category, i) => {
          return (
            <Button
              className={styles.link}
              href={String(category?.full_slug?.split("products/")[1])}
              key={i}
              style={{ display: "flex", flexDirection: "column" }}
            >
              <p>{category.name}</p>
            </Button>
          );
        })}
      </div>
    </section>
  );
};

export const SearchContentResults = ({
  contentLinks,
}: {
  contentLinks: ContentLink[] | undefined;
}) => {
  const t = useTranslations();
  if (!contentLinks || contentLinks.length === 0) return null;

  return (
    <section className={styles.productSection}>
      <h2 className={styles.searchTitle}>{t("Let us help")}</h2>
      <div
        /*  style={{ paddingBottom: "32px" }} */
        className={styles.searchSuggestions}
      >
        {contentLinks?.map((link, i) => {
          return (
            <Link key={i} className={styles.contentLink} href={link.page_url}>
              {link.title}
            </Link>
          );
        })}
      </div>
    </section>
  );
};

export const SearchModalTrigger = ({
  asChild,
  children,
  ...props
}: ComponentProps<"button"> & { asChild?: boolean }) => {
  const setOpen = useSearchModal((state) => state.setOpen);
  const Component = asChild ? Slot : "button";
  const pathname = usePathname();

  const handleOpen = () => {
    // Noop if we are on the search page
    if (pathname.includes("/search")) return;
    setOpen(true);
  };

  return (
    <Component {...props} onClick={handleOpen}>
      {children}
    </Component>
  );
};

export const SearchClose = ({
  className,
  ...props
}: ComponentProps<"button">) => {
  const setOpen = useSearchModal((state) => state.setOpen);
  return (
    <button
      className={styles.searchClose}
      {...props}
      onClick={() => setOpen(false)}
    >
      <Close />
    </button>
  );
};

/**
 * Chrome is being ridiculous and keeps the previous render while painting the new one.
 * Adding this element to the DOM to force the browser to paint the new render.
 */
const DoNotRemove = () => (
  <p
    style={{
      color: "var(--neutral-2)",
      userSelect: "none",
      pointerEvents: "none",
    }}
    aria-hidden="true"
  >
    .
  </p>
);
